<script setup lang="ts">
import { Form } from 'ant-design-vue';
import { debounce, get, isEqual } from 'lodash-es';
import { computed, ref, watch } from 'vue';
import { request } from '@/service/request';

defineOptions({
  name: 'MyTreeSelect'
});

interface MyTreeSelectProps {
  api?: string;
  value?: any;
  // 请求体中
  searchParams?: Record<string, any>;
  // url中
  queryParams?: Record<string, any>;
  dataKey?: string | string[];
  requestMethod?: 'get' | 'post';
  labelKey?: string | string[];
  valueKey?: string | string[];
  onResponse?: (response: Record<string, any>) => any[];
  labelInValue?: boolean;
  searchKey?: string;
  childrenKey?: string;
  isRemoteSearch?: boolean;
}

const {
  api,
  searchParams,
  queryParams,
  dataKey = ['data'],
  requestMethod = 'get',
  onResponse,
  labelKey = 'name',
  valueKey = 'id',
  value,
  labelInValue = false,
  isRemoteSearch = false,
  searchKey = 'name',
  childrenKey = 'children'
} = defineProps<MyTreeSelectProps>();

const { onFieldChange } = Form.useInjectFormItemContext();
const dataSource = ref<any[]>([]);
const loading = ref<boolean>(false);
const emit = defineEmits<{
  (e: 'update:value', selectedValue: any): void;
}>();

watch(
  [() => api, () => searchParams, () => queryParams],
  (newVal, oldVal) => {
    if (!isEqual(newVal, oldVal)) {
      getDataSource({});
    }
  },
  {
    immediate: true
  }
);

async function getDataSource(params: Record<string, any> = {}) {
  let bodyParams = {
    ...searchParams
  };
  let urlParams = {
    ...queryParams
  };
  if (requestMethod !== 'get') {
    bodyParams = {
      ...bodyParams,
      ...params
    };
  } else {
    urlParams = {
      ...urlParams,
      ...params
    };
  }
  try {
    loading.value = true;
    const res = await request<any>({
      url: api,
      method: requestMethod,
      data: bodyParams,
      params: urlParams
    });
    if (onResponse) {
      const data = onResponse(res);
      dataSource.value = data;
    } else {
      const d = get(res, dataKey, []);
      dataSource.value = d;
    }
  } catch (error) {
    console.log(error);
    dataSource.value = [];
  } finally {
    loading.value = false;
  }
}

/**
 * 格式化树形数据结构
 *
 * @param data 原始数据
 * @param labelKey 标签字段名
 * @param valueKey 值字段名
 * @param childrenKey 子节点字段名
 * @returns 格式化后的树形数据
 */
function formatTreeData(data: any[]): any[] {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  return data.map(item => {
    const child = item?.[childrenKey];
    const formattedNode = {
      label: get(item, labelKey),
      value: get(item, valueKey),
      title: get(item, labelKey),
      extra: item,
      children: [] as any[]
    };

    // 如果有子节点，递归处理
    if (child && Array.isArray(child)) {
      formattedNode.children = formatTreeData(child);
    }

    return formattedNode;
  });
}

const treeData = computed(() => {
  return formatTreeData(dataSource.value);
});

function onChange(val: any) {
  emit('update:value', val);
  onFieldChange?.();
}

function handleSearch(val: string) {
  if (isRemoteSearch) {
    debounce(() => {
      getDataSource({ [searchKey]: val });
    }, 300)();
  }
}
</script>

<template>
  <ATreeSelect
    :value="value"
    :loading="loading"
    :tree-data="treeData"
    :label-in-value="labelInValue"
    :filter-option="!isRemoteSearch"
    @change="onChange"
    @search="handleSearch"
  >
    <slot></slot>
  </ATreeSelect>
</template>

<style lang="scss" scoped></style>
